// components/VerticalSlicer.tsx
import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { useDrag } from '@use-gesture/react';

const Container = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
`;

const Pane = styled.div.withConfig({
  shouldForwardProp: (prop) => !['width'].includes(prop),
})<{ width: number }>`
  width: ${({ width }) => width}%;
  height: 100%;
  position: relative;
`;

const Divider = styled.div.withConfig({
  shouldForwardProp: (prop) => !['dragging'].includes(prop),
})<{ dragging?: boolean }>`
  position: absolute;
  width: 2px;
  height: 100%;
  transform: translateX(-50%);
  cursor: ew-resize;
  background: rgba(128, 128, 128, 0.3);
  z-index: 10;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.2);
  transition:
    background,
    width 0.2s;

  &:hover {
    width: 4px;
    background: rgba(128, 128, 128, 0.6);
  }

  ${({ dragging }) =>
    dragging &&
    `
    width: 4px;
    background: rgba(146, 144, 230, 0.8) !important;
  `}
`;

type VerticalSlicerProps = {
  left: React.ReactNode;
  right: React.ReactNode;
  initialLeftPercent?: number;
  minPercent?: number;
  maxPercent?: number;
  onChange?: (percent: number) => void;
};

/**
 * A vertical splitter component that allows resizing of two panes.
 * @returns The vertical slicer component.
 */
export const VerticalSlicer: React.FC<VerticalSlicerProps> = ({
  left,
  right,
  initialLeftPercent = 50,
  minPercent = 5,
  maxPercent = 95,
  onChange,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [leftWidth, setLeftWidth] = useState(initialLeftPercent);
  const [down, setDown] = useState(false);
  const bind = useDrag(
    ({ movement: [mx], memo = leftWidth, down }) => {
      if (!containerRef.current) return memo;

      const containerWidth = containerRef.current.offsetWidth;
      const deltaPercent = (mx / containerWidth) * 100;
      const newWidth = Math.min(maxPercent, Math.max(minPercent, memo + deltaPercent));

      setLeftWidth(newWidth);
      setDown(down);
      onChange?.(newWidth);

      return memo;
    },
    { axis: 'x', pointer: { buttons: [1] } },
  );

  return (
    <Container ref={containerRef}>
      <Pane width={leftWidth}>{left}</Pane>
      <Divider {...bind()} style={{ left: leftWidth + '%' }} dragging={down} />
      <Pane width={100 - leftWidth}>{right}</Pane>
    </Container>
  );
};
